import {SPHERE} from 'src/app/models/patient-sphere/sphere.namespace';
import {DDRTypeEnum} from '../../class/micronutrient-ddr-factory/ddr-type.enum';
import {CardiovascularRiskEnum, ExerciseTimeSinceStartEnum, PhysicalActivityLevelEnum} from '../../enums/record';
import {MeasurementTypeCategoryEnum} from '../../models/measurement';
import {Pipe, PipeTransform} from '@angular/core';
import {AppointmentStatusEnum, AppointmentTypeEnum} from '../../models/appointment';
import {CivilStateEnum, PatientStatusEnum} from '../../models/patient';
import {GenderEnum} from '../../models/user';
import {PhoneTypeEnum} from '../../enums/phone';
import {ToursEnum} from '../../enums/tour';
import {MacronutrientDistributionTypeEnum} from '../../class/macronutrient-distribution-strategy/enums/macronutrient-distribution-type.enum';
import {CalorieTrackingNamespace} from '../../models/meal-plan-builder';
import {MedicalPersonalDiseaseControlEnum} from '../../enums/medical-personal/medical-personal-disease-control.enum';

@Pipe({
  name: 'getTranslateEnum'
})
export class GetTranslateEnumPipe implements PipeTransform {

  transform(value: string, type: string): string {
    if (!value) return '';

    switch (type) {
      case 'gender':
        return this.translateGender(value);
      case 'phoneType':
        return this.translatePhoneType(value);
      case 'macronutrients':
        return this.translateMacronutrients(value);
      case 'civilState':
        return this.translateCivilState(value);
      case 'appointmentStatus':
        return this.translateAppointmentStatus(value);
      case 'appointmentStatusPlural':
        return this.translateAppointmentStatusPlural(value);
      case 'appointmentTypePlural':
        return this.translateAppointmentTypePlural(value);
      case 'sedentaryLevel':
        return this.translateSedentaryLevel(value);
      case 'appointmentType':
        return this.translateAppointmentType(value);
      case 'patientStatusTypePlural':
        return this.translatePatientStatusTypePlural(value);
      case 'exerciseTimeSinceStart':
        return this.translateExerciseTimeSinceStart(value);
      case 'physicalActivityLevel':
        return this.translatePhysicalActivityLevel(value);
      case 'habitsFrequency':
        return this.translateHabitsFrequency(value);
      case 'measurementsCategory':
        return this.translateMeasurementsCategory(value);
      case 'patientGoalSearchDescription':
        return this.translatePatientGoalSearchDescription(value);
      case 'cardiovascularRisk':
        return this.translateCardiovascularRisk(value);
      case 'ddrStrategy':
        return this.translateDdrStrategy(value);
      case 'invoiceStatus':
        return this.translateInvoiceStatus(value);
      case 'tours':
        return this.translateTours(value as ToursEnum);
      case 'macronutrientsDistribution':
        return this.translateMacronutrientsDistribution(value as MacronutrientDistributionTypeEnum);
      case 'calorieTrackingType':
        return this.trackingCalorieType(value as CalorieTrackingNamespace.MeasurementType);
      case 'medicalDiseaseControl':
        return this.medicalDiseaseControl(value as MedicalPersonalDiseaseControlEnum);
    }
  }

  private trackingCalorieType(value: CalorieTrackingNamespace.MeasurementType): string {
    switch (value) {
      case 'custom': return 'personalizado';
      case 'get': return 'get'; // gasto energético total
      case 'geb': return 'geb'; // gasto energético basal
      case 'tmb': return 'tmb'; // taza metabólica basal
      default: return '-';
    }
  }

  private medicalDiseaseControl(value: MedicalPersonalDiseaseControlEnum): string {
    switch (value) {
      case 'WELL': return 'bueno';
      case 'FAIR': return 'regular';
      case 'BAD': return 'malo';
      default: return '-';
    }
  }

  private translateMacronutrientsDistribution(value: MacronutrientDistributionTypeEnum): string {
    switch (value) {
      case MacronutrientDistributionTypeEnum.MX: {
        return 'Oficial Mexicana NOM-043-SSA2-2012';
      }
      case MacronutrientDistributionTypeEnum.SMAE: {
        return 'Sistema Mexicano de Alimentos Equivalentes';
      }
      case MacronutrientDistributionTypeEnum.OMS: {
        return 'OMS';
      }
      case MacronutrientDistributionTypeEnum.DGA_USDA_HHS: {
        return 'USDA-HHS Guías Alimentarias para los Estadounidenses';
      }
      default: { return '-'; }
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translateInvoiceStatus(value: string): string {
    switch (value) {
      case 'paid':
        return 'Pagado';
      case 'active':
        return 'Activo';
      case 'canceled':
        return 'Cancelado';
      case 'incomplete':
        return 'Incompleto';
      case 'unpaid':
        return 'No pagado';
      case 'trialing':
        return 'Prueba';
      case 'past_due':
        return 'Vencido';
      case 'open':
        return 'Vencido';
    }
  }

  /**
   * translate tours
   * @param tour
   * @private
   */
  private translateTours(tour: ToursEnum): string {
    switch (tour) {
      case ToursEnum.REGISTER_PATIENT: { return 'Crear paciente'; }
      case ToursEnum.REGISTER_BRANCH: { return 'Crear sucursal'; }
      case ToursEnum.REGISTER_FOOD_DISH: { return 'Crear receta'; }
      case ToursEnum.REGISTER_MEAL_PLAN: { return 'Crear plan'; }
      case ToursEnum.CHECK_SCHEDULE: { return 'Revisar agenda'; }
      case ToursEnum.SCHEDULE_APPOINTMENT: { return 'Crear cita'; }
      case ToursEnum.SPHERE: { return 'Esfera'; }
      case ToursEnum.SCHEDULES: { return 'Horarios'; }
      case ToursEnum.EXPERT_SCHEDULES: { return 'Horarios'; }
      case ToursEnum.ADVICE: { return 'Asesoría'; }
      case ToursEnum.ASSIGN_CREATE_MEAL_PLAN: { return 'Crear y asignar plan'; }
      case ToursEnum.ASSIGN_SELECT_MEAL_PLAN: { return 'Seleccionar y asignar plan'; }
      default: { return '---'; }
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translateDdrStrategy(value: string): string {
    switch (value) {
      case DDRTypeEnum.LACTATINGMOTHER18:
        return 'Madres lactantes 18 años';
      case DDRTypeEnum.LACTATINGMOTHER1930:
        return 'Madres lactantes 19 a 30 años';
      case DDRTypeEnum.LACTATINGMOTHER3150:
        return 'Madres lactantes 31 a 50 años';
      case DDRTypeEnum.PREGNANT18:
        return 'Embarazadas 18 años';
      case DDRTypeEnum.PREGNANT1930:
        return 'Embarazadas 19 a 30 años';
      case DDRTypeEnum.PREGNANT3150:
        return 'Embarazadas 31 a 50 años';
      case DDRTypeEnum.MEN913:
        return 'Hombres 9 a 13 años';
      case DDRTypeEnum.MEN1418:
        return 'Hombres 14 a 18 años';
      case DDRTypeEnum.MEN1930:
        return 'Hombres 19 a 30 años';
      case DDRTypeEnum.MEN3150:
        return 'Hombres 31 a 50 años';
      case DDRTypeEnum.MEN5170:
        return 'Hombres 51 a 70 años';
      case DDRTypeEnum.MEN70:
        return 'Hombres 70 años';
      case DDRTypeEnum.WOMAN913:
        return 'Mujeres 9 a 13 años';
      case DDRTypeEnum.WOMAN1418:
        return 'Mujeres 14 a 18 años';
      case DDRTypeEnum.WOMAN1930:
        return 'Mujeres 19 a 30 años';
      case DDRTypeEnum.WOMAN3150:
        return 'Mujeres 31 a 50 años';
      case DDRTypeEnum.WOMAN5170:
        return 'Mujeres 51 a 70 años';
      case DDRTypeEnum.WOMAN70:
        return 'Mujeres 70 años';
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translateCardiovascularRisk(value: string): string {
    switch (value) {
      case CardiovascularRiskEnum.MILD:
        return 'Leve';
      case CardiovascularRiskEnum.HIGH:
        return 'Alto';
      case CardiovascularRiskEnum.VERY_HIGH:
        return 'Muy alto';
      case CardiovascularRiskEnum.MODERATE:
        return 'Moderado';
      case CardiovascularRiskEnum.VERY_SLIGHT:
        return 'Muy leve';
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translatePatientGoalSearchDescription(value: string): string {
    switch (value) {
      case MeasurementTypeCategoryEnum.ANALYTICS:
        return 'ANALÍTICAS';
      case MeasurementTypeCategoryEnum.ANTHROPOMETRIC:
        return 'ANTROPOMÉTRICAS';
      case MeasurementTypeCategoryEnum.BODY_COMPOSITION:
        return 'COMPOSICIÓN CORPORAL';
      case MeasurementTypeCategoryEnum.INTERNAL:
        return 'INTERNA';
      case 'RECORD_BIOCHEMICAL':
        return 'FICHA BIOQUÍMICOS';
      case 'RECORD_PHYSICAL_ACTIVITY':
        return 'FICHA ACTIVIDAD FÍSICA';
      case 'RECORD_OTHERS_HABITS':
        return 'FICHA DE OTROS HÁBITOS';
      default:
        return value;
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translateMeasurementsCategory(value: string): string {
    switch (value) {
      case MeasurementTypeCategoryEnum.ANALYTICS:
        return 'ANALÍTICAS';
      case MeasurementTypeCategoryEnum.ANTHROPOMETRIC:
        return 'ANTROPOMÉTRICAS';
      case MeasurementTypeCategoryEnum.BODY_COMPOSITION:
        return 'COMPOSICIÓN CORPORAL';
      case MeasurementTypeCategoryEnum.INTERNAL:
        return 'INTERNA';
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translateHabitsFrequency(value: string): string {
    switch (value) {
      case '0':
        return 'No lo acostumbra';
      case '1':
        return '1 a 2 días a la semana';
      case '3':
        return '3 a 4 días a la semana';
      case '5':
        return '5 a 7 días a la semana';
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translatePhysicalActivityLevel(value: string): string {
    switch (value) {
      case PhysicalActivityLevelEnum.NO_ACTIVITY:
        return 'Sin actividad';
      case PhysicalActivityLevelEnum.LIGHT_EXERCISE:
        return 'Ejercicio ligero';
      case PhysicalActivityLevelEnum.MODERATE_EXERCISE:
        return 'Ejercicio moderado';
      case PhysicalActivityLevelEnum.HEAVY_EXERCISE:
        return 'Ejercicio pesado';
      case PhysicalActivityLevelEnum.VERY_HEAVY_EXERCISE:
        return 'Ejercicio muy pesado';
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translateExerciseTimeSinceStart(value: string): string {
    switch (value) {
      case ExerciseTimeSinceStartEnum.LESS_THAN_MONTH:
        return 'Menos del mes';
      case ExerciseTimeSinceStartEnum.LESS_THAN_THREE_MONTHS:
        return 'Menos de 3 meses';
      case ExerciseTimeSinceStartEnum.LESS_THAN_ONE_YEAR:
        return 'Menos de un año';
      case ExerciseTimeSinceStartEnum.MORE_THAN_ONE_YEAR:
        return 'Más de un año';
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translatePatientStatusTypePlural(value: string): string {
    switch (value) {
      case PatientStatusEnum.PRECREATED:
        return 'Invitados';
      case PatientStatusEnum.ACTIVE:
        return 'Activos';
      default:
        return 'Activos';
      /* case PatientStatusEnum.DISABLED:
        translate = 'Deshabilitados';
        break; */
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translateAppointmentType(value: string): string {
    switch (value) {
      case AppointmentTypeEnum.ON_SITE:
        return 'Presencial';
      case AppointmentTypeEnum.ONE_TO_ONE:
        return 'Videoconferencia';
      default:
        return 'Presencial';
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translateSedentaryLevel(value: string): string {
    switch (value) {
      case SPHERE.SphereSedentaryLevelEnum.LIGHT:
        return 'Ligero';
      case SPHERE.SphereSedentaryLevelEnum.MEDIUM:
        return 'Moderado';
      case SPHERE.SphereSedentaryLevelEnum.HIGH:
        return 'Alto';
      default:
        return 'Ligero';
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translateAppointmentTypePlural(value: string): string {
    switch (value) {
      case AppointmentTypeEnum.ON_SITE:
        return 'Presenciales';
      case AppointmentTypeEnum.ONE_TO_ONE:
        return 'Videoconferencias';
      default:
        return 'Presenciales';
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translateAppointmentStatusPlural(value: string): string {
    switch (value) {
      case AppointmentStatusEnum.CANCELLED:
        return 'Canceladas';
      case AppointmentStatusEnum.COMPLETED:
        return 'Completadas';
      case AppointmentStatusEnum.TO_RESCHEDULE:
        return 'Reprogramadas';
      case AppointmentStatusEnum.SCHEDULED:
        return 'Programadas';
      case AppointmentStatusEnum.CONFIRMED:
        return 'Confirmadas';
      case AppointmentStatusEnum.TO_CONFIRM:
        return 'Por confirmar';
      default:
        return 'Programadas';
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translateAppointmentStatus(value: string): string {
    switch (value) {
      case AppointmentStatusEnum.CANCELLED:
        return 'Cancelada';
      case AppointmentStatusEnum.COMPLETED:
        return 'Completada';
      case AppointmentStatusEnum.SCHEDULED:
        return 'Programada';
      case AppointmentStatusEnum.TO_RESCHEDULE:
        return 'Reprogramada';
      case AppointmentStatusEnum.CONFIRMED:
        return 'Confirmada';
      case AppointmentStatusEnum.TO_CONFIRM:
        return 'Por confirmar';
      default:
        return 'Programada';
    }
  }

  /**
   *
   * @param value
   */
  private translateCivilState(value: string): string {
    switch (value) {
      case CivilStateEnum.DIVORCEE:
        return 'Divorciado(a)';
      case CivilStateEnum.IN_RELATIONSHIP:
        return 'En una relación';
      case CivilStateEnum.MARRIED:
        return 'Casado(a)';
      case CivilStateEnum.SINGLE:
        return 'Soltero(a)';
      case CivilStateEnum.WINDOWED:
        return 'Viudo(a)';
      default:
        return 'Soltero(a)';
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translateMacronutrients(value: string): string {
    switch (value) {
      case 'carbohydrates':
         return 'carbohydrate_g';
      case 'lipids':
         return 'totalFat_g';
      case 'protein':
         return 'protein_g';
      case 'fiber':
         return 'dietaryFiber_g';
      default:
        return 'carbohydrate_g';
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translatePhoneType(value: string): string {
    switch (value) {
      case PhoneTypeEnum.FAX:
        return 'Fax';
      case PhoneTypeEnum.LOCAL:
        return 'Local';
      case PhoneTypeEnum.CELLULAR:
        return 'Celular';
      default:
        return 'Celular';
    }
  }

  /**
   *
   * @param value
   * @private
   */
  private translateGender(value: string): string {
    switch (value) {
      case GenderEnum.FEMALE:
        return 'Femenino';
      case GenderEnum.MALE:
        return 'Masculino';
      default:
        return 'Masculino';
    }
  }

}
